import { useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { useToast } from '@givebutter/primitives/toast';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AddChapterDialogFormValues, createChapter, getSearchSchoolResults } from 'api/chapter';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { ListItem } from 'components/list-item';
import * as Select from 'components/select';
import { SelectItemWithLabel } from 'components/select';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useForceRender } from 'customHooks/useForceRender';
import { omit } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { chapterQueryKeys } from 'queries/chapter-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface SearchResultItem {
    id: number;
    name: string;
}

interface AddChapterDialogProps extends Dialog.DialogProps {}

type FormValue = string | number | null;

const AddChapterDialog = (props: AddChapterDialogProps) => {
    const toast = useToast();
    const [closing, setClosing] = useState(false);
    const { children, ...rest } = props;

    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { key, forceRender } = useForceRender();

    const methods = Form.useGivebutterForm<AddChapterDialogFormValues>({
        defaultValues: {
            name: '',
            members: '',
            role: 'admin',
            schoolId: null,
            schoolName: '',
            isPartOfSchool: false,
        },
    });

    const { control, handleSubmit, setValue, path, getValues, reset } = methods;

    const [schoolName, isPartOfSchool] = useWatch({
        control,
        name: ['schoolName', 'isPartOfSchool'],
    });

    const debouncedSchoolName = useDebounce(schoolName);

    const { data: searchResults } = useSuspenseQuery({
        queryKey: chapterQueryKeys.chapterSchoolsWithFilters({ query: debouncedSchoolName }),
        queryFn: () => getSearchSchoolResults(debouncedSchoolName),
    });

    const { mutate: submit, isPending: isLoading } = useMutation({
        mutationFn: () => {
            return createChapter(accountId, valuesToSubmit(getValues()));
        },

        onSuccess: () => {
            toast.success(
                <>
                    <span className="text-semibold">Success!</span> Added chapter successfully.
                </>,
            );

            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.chapters(accountId),
                type: 'all',
            });
            reset();
            if (closing) forceRender();
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(
                error.response?.data.message ?? (
                    <>
                        <span className="text-semibold">Whoops.</span> Something went wrong. Please
                        try again.
                    </>
                ),
            );
        },
        onSettled: () => {
            setClosing(false);
        },
    });

    const onChangeValue = (name: keyof AddChapterDialogFormValues, value: FormValue) => {
        setValue(name, value);
    };

    const valuesToSubmit = (values: AddChapterDialogFormValues) => {
        const formValues = omit(values, 'isPartOfSchool');

        return {
            ...formValues,
            schoolId: formValues.schoolId || '',
        };
    };

    return (
        <Dialog.Root key={key} {...rest}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className="p-6">
                    <Dialog.Close />
                    <Dialog.Title className="pb-6 text-base font-medium">
                        Create a Chapter
                    </Dialog.Title>
                    <Form.Provider {...methods}>
                        <form
                            onSubmit={e => {
                                e.preventDefault();
                                handleSubmit(() => submit())();
                            }}
                        >
                            <div className="flex flex-col gap-8">
                                <div className="flex flex-col gap-2">
                                    <Form.Label
                                        className="text-base font-medium"
                                        name={path('name')}
                                    >
                                        Chapter Name
                                    </Form.Label>
                                    <Form.TextInputRoot
                                        control={control}
                                        name="name"
                                        rules={{ required: 'This field is required' }}
                                    >
                                        <Form.TextInputForm placeholder="Required" />
                                    </Form.TextInputRoot>
                                    <Form.Error name={path('name')} />
                                    <div className="flex items-center">
                                        <div className="flex pr-2">
                                            <Form.UseFormCheckboxInput
                                                control={control}
                                                name="isPartOfSchool"
                                                onCheckedChange={isChecked => {
                                                    if (!isChecked) {
                                                        setValue('schoolId', null);
                                                        setValue('schoolName', '');
                                                    }
                                                }}
                                            />
                                        </div>
                                        <Form.Label
                                            className="text-base font-normal"
                                            name={path('isPartOfSchool')}
                                        >
                                            This chapter is located or is part of a school /
                                            university
                                        </Form.Label>
                                    </div>
                                </div>
                                {isPartOfSchool && (
                                    <div className="flex flex-col gap-2">
                                        <Label
                                            className="text-base font-medium"
                                            htmlFor="schools-search"
                                        >
                                            School Name
                                        </Label>
                                        <Select.SearchableSelect
                                            localSearch={false}
                                            onSelectItem={(school: SelectItemWithLabel | null) => {
                                                if (school) {
                                                    onChangeValue('schoolName', school.label);
                                                    onChangeValue('schoolId', parseInt(school.id));
                                                }
                                            }}
                                            onInputChange={(value: string) => {
                                                onChangeValue('schoolName', value);
                                            }}
                                            disabled={isLoading}
                                            inputProps={{
                                                placeholder: 'Search School',
                                                id: 'schools-search',
                                            }}
                                        >
                                            {searchResults?.length === 0 && (
                                                <ListItem>No schools found.</ListItem>
                                            )}
                                            {searchResults?.map((item: SearchResultItem) => (
                                                <Select.ListItem
                                                    key={item.id}
                                                    value={{
                                                        label: item.name,
                                                        id: item.id.toString(),
                                                    }}
                                                >
                                                    {item.name}
                                                </Select.ListItem>
                                            ))}
                                        </Select.SearchableSelect>
                                    </div>
                                )}
                                <div className="flex flex-col gap-2">
                                    <Form.Label
                                        className="text-base font-medium leading-5"
                                        name={path('members')}
                                    >
                                        Administrator (Optional)
                                    </Form.Label>
                                    <p className="text-md leading-5">
                                        Invite the chapter’s admin to manage this sub-account. You
                                        can always add more later.
                                    </p>
                                    <Form.TextInputRoot control={control} name="members">
                                        <Form.TextInputForm placeholder="Provide a comma separated list of email addresses" />
                                    </Form.TextInputRoot>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <Button
                                        variant="solid"
                                        type="submit"
                                        disabled={isLoading && !closing}
                                    >
                                        {isLoading && !closing ? (
                                            <FontAwesomeIcon icon={faSpinnerThird} spin />
                                        ) : (
                                            'Create and add another'
                                        )}
                                    </Button>
                                    <Button
                                        variant="transparent"
                                        disabled={isLoading && closing}
                                        onClick={() => {
                                            setClosing(true);
                                            handleSubmit(() => submit())();
                                        }}
                                    >
                                        Create and close
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </Form.Provider>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const AddChapterDialogTrigger = Dialog.Trigger;
const Root = AddChapterDialog;

const Trigger = AddChapterDialogTrigger;

export { AddChapterDialog, AddChapterDialogTrigger, Root, Trigger };
